import type { CSSProperties } from 'react';
import { orgInitials } from './orgFormat';

export type OrgAvatarTone = 'accent' | 'neutral' | 'muted';

interface OrgAvatarProps {
    name: string;
    size: number;
    radius?: number;
    tone?: OrgAvatarTone;
    /** Overrides `tone` with a literal color (the detail header uses the
     * org's own branding.primary_color — frame 22 line 52 — rather than the
     * generic accent/neutral/muted set). */
    background?: string;
    color?: string;
    fontSize?: number;
}

/**
 * EAA-1815: the org identity tile shared by the list table (30px), the
 * quick-peek drawer header (40px) and the detail page header (48px).
 *
 * `tone` picks from the SAME palette the frames already establish for
 * avatars elsewhere in this app (identity-teal = --color-primary-tint /
 * --color-primary-hover, neutral = --color-surface-sunken /
 * --color-text-secondary) rather than inventing new tokens — see
 * OrganizationsTable for which rows get which tone.
 */
export function OrgAvatar({
    name,
    size,
    radius,
    tone = 'neutral',
    background,
    color,
    fontSize,
}: OrgAvatarProps) {
    const palette: Record<OrgAvatarTone, { bg: string; fg: string }> = {
        accent: { bg: 'var(--color-primary-tint)', fg: 'var(--color-primary-hover)' },
        neutral: { bg: 'var(--color-surface-sunken)', fg: 'var(--color-text-secondary)' },
        muted: { bg: 'var(--color-surface-sunken)', fg: 'var(--color-text-muted)' },
    };
    const resolved = palette[tone];

    const style: CSSProperties = {
        width: size,
        height: size,
        borderRadius: radius ?? Math.round(size * 0.225),
        background: background ?? resolved.bg,
        color: color ?? resolved.fg,
        fontSize: fontSize ?? Math.round(size * 0.34),
    };

    return (
        <span className="portal-org-avatar" style={style} aria-hidden="true">
            {orgInitials(name)}
        </span>
    );
}

export default OrgAvatar;
